import type { GeneratedRoutePoint } from "../data/rideService";
import { loadGoogleMaps } from "./maps";

const ELEVATION_BATCH_SIZE = 350;
const ELEVATION_BATCH_DELAY_MS = 120;
const MIN_ELEVATION_COVERAGE_RATIO = 0.8;

type ElevationResult = {
  elevation: number;
  location?: { lat: () => number; lng: () => number };
};

type ElevationService = {
  getElevationForLocations: (
    request: { locations: Array<{ lat: number; lng: number }> },
    callback: (results: ElevationResult[] | null, status: string) => void
  ) => void;
};

type GoogleMapsElevationApi = {
  maps?: {
    ElevationService?: new () => ElevationService;
    ElevationStatus?: { OK?: string };
  };
};

export type RouteElevationStats = {
  elevationGainMeters: number;
  elevationLossMeters: number;
  minAltitudeMeters: number;
  maxAltitudeMeters: number;
};

export type RouteElevationResult = {
  fetched: boolean;
  points: GeneratedRoutePoint[];
  stats: RouteElevationStats;
};

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export function routePointAltitudeMeters(point: GeneratedRoutePoint) {
  const candidate = point as GeneratedRoutePoint & { altitudeMeters?: number };
  if (typeof candidate.altitude === "number" && Number.isFinite(candidate.altitude)) {
    return candidate.altitude;
  }
  if (typeof candidate.altitudeMeters === "number" && Number.isFinite(candidate.altitudeMeters)) {
    return candidate.altitudeMeters;
  }
  return null;
}

export function withCanonicalAltitude(point: GeneratedRoutePoint, altitude: number | null): GeneratedRoutePoint {
  if (altitude == null || !Number.isFinite(altitude)) {
    return { ...point };
  }

  return {
    ...point,
    altitude,
    altitudeMeters: altitude
  };
}

export function routeHasUsableElevation(points: GeneratedRoutePoint[]) {
  if (points.length < 2) return false;
  const altitudeCount = points.reduce((count, point) => count + (routePointAltitudeMeters(point) == null ? 0 : 1), 0);
  return altitudeCount >= Math.max(2, Math.ceil(points.length * MIN_ELEVATION_COVERAGE_RATIO));
}

export function calculateRouteElevationStats(points: GeneratedRoutePoint[]): RouteElevationStats | null {
  let elevationGainMeters = 0;
  let elevationLossMeters = 0;
  let minAltitudeMeters = Number.POSITIVE_INFINITY;
  let maxAltitudeMeters = Number.NEGATIVE_INFINITY;
  let previousAltitude: number | null = null;
  let hasAltitude = false;

  for (const point of points) {
    const altitude = routePointAltitudeMeters(point);
    if (altitude == null) {
      previousAltitude = null;
      continue;
    }

    hasAltitude = true;
    minAltitudeMeters = Math.min(minAltitudeMeters, altitude);
    maxAltitudeMeters = Math.max(maxAltitudeMeters, altitude);

    if (previousAltitude != null) {
      const delta = altitude - previousAltitude;
      if (delta > 0) elevationGainMeters += delta;
      if (delta < 0) elevationLossMeters += Math.abs(delta);
    }
    previousAltitude = altitude;
  }

  return hasAltitude
    ? {
        elevationGainMeters,
        elevationLossMeters,
        minAltitudeMeters,
        maxAltitudeMeters
      }
    : null;
}

export function normalizeRouteElevation(points: GeneratedRoutePoint[]): RouteElevationResult | null {
  if (!routeHasUsableElevation(points)) return null;
  const normalizedPoints = points.map((point) => withCanonicalAltitude(point, routePointAltitudeMeters(point)));
  const stats = calculateRouteElevationStats(normalizedPoints);
  return stats ? { fetched: false, points: normalizedPoints, stats } : null;
}

export function routeElevationHash(points: GeneratedRoutePoint[]) {
  let hash = 2166136261;
  for (const point of points) {
    const lat = Math.round(point.latitude * 1_000_000);
    const lng = Math.round(point.longitude * 1_000_000);
    hash ^= lat;
    hash = Math.imul(hash, 16777619);
    hash ^= lng;
    hash = Math.imul(hash, 16777619);
  }
  return `${points.length}:${hash >>> 0}`;
}

async function fetchElevationBatch(service: ElevationService, locations: Array<{ lat: number; lng: number }>) {
  const googleMaps = window.google as GoogleMapsElevationApi | undefined;
  const okStatus = googleMaps?.maps?.ElevationStatus?.OK ?? "OK";

  return new Promise<ElevationResult[]>((resolve, reject) => {
    service.getElevationForLocations({ locations }, (results, status) => {
      if (status === okStatus && results && results.length === locations.length) {
        resolve(results);
      } else {
        reject(new Error(status || "elevation-failed"));
      }
    });
  });
}

export async function enrichRoutePointsWithElevation(
  points: GeneratedRoutePoint[],
  onProgress?: (completedPoints: number, totalPoints: number) => void
): Promise<RouteElevationResult> {
  const normalized = normalizeRouteElevation(points);
  if (normalized) return normalized;

  await loadGoogleMaps();
  const googleMaps = window.google as GoogleMapsElevationApi | undefined;
  if (!googleMaps?.maps?.ElevationService) {
    throw new Error("elevation-service-unavailable");
  }

  const service = new googleMaps.maps.ElevationService();
  const elevations: number[] = [];

  for (let startIndex = 0; startIndex < points.length; startIndex += ELEVATION_BATCH_SIZE) {
    const batch = points.slice(startIndex, startIndex + ELEVATION_BATCH_SIZE);
    const locations = batch.map((point) => ({ lat: point.latitude, lng: point.longitude }));
    const results = await fetchElevationBatch(service, locations);
    elevations.push(...results.map((result) => result.elevation));
    onProgress?.(Math.min(elevations.length, points.length), points.length);

    if (startIndex + ELEVATION_BATCH_SIZE < points.length) {
      await sleep(ELEVATION_BATCH_DELAY_MS);
    }
  }

  const enrichedPoints = points.map((point, index) =>
    withCanonicalAltitude(point, Number.isFinite(elevations[index]) ? elevations[index] : routePointAltitudeMeters(point))
  );
  const stats = calculateRouteElevationStats(enrichedPoints);
  if (!stats) throw new Error("elevation-empty");

  return {
    fetched: true,
    points: enrichedPoints,
    stats
  };
}
